import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { FormItem } from './FormItem'
import { Checkbox, DatePicker, Input, InputNumber, Select, Form as AntdForm } from 'antd';
import { Control, FieldErrors, UseFormSetValue, Controller } from 'react-hook-form';
import Editor from './Editor';
import JSONField from './JSONField'
import ArrayGroup from './ArrayGroup';
import { useParams } from '@umijs/max';
import dayjs from 'dayjs';
import { unifiedClient } from '@/lib/unified-fetch';

type ModelFieldProps = {
  field: API.FieldType
  control: Control<API.defaultValueType>
  setValue?: UseFormSetValue<API.defaultValueType>
  errors?: FieldErrors<API.defaultValueType>
}

// Debounce hook
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// Custom hook for API fetching with search support
const useApiOptions = (field: API.FieldType) => {
  const [options, setOptions] = useState<Array<{label: string; value: string | number}>>([]);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const debouncedSearchValue = useDebounce(searchValue, 300);
  
  // Merge pre-existing static options with API options, avoiding duplicates
  const mergedOptions = useMemo(() => {
    const staticOptions = field.data || [];
    const apiOptionsWithoutDuplicates = options.filter(apiOption => 
      !staticOptions.some(staticOption => staticOption.value === apiOption.value)
    );
    return [...staticOptions, ...apiOptionsWithoutDuplicates];
  }, [options, field.data]);

  const fetchOptions = useCallback(async (searchTerm = '') => {
    if (field.type !== 'select' || !field.apiEndpoint) return;

    try {
      setLoading(true);
      const isAdminApi = field.isAdminApi !== false; // Default to true
      const labelField = field.labelField || 'name';
      const valueField = field.valueField || 'id';
      const searchField = field.searchField || labelField;

      const client = isAdminApi ? unifiedClient.admin : unifiedClient.app;

      let response: any;
      
      if (isAdminApi) {
        // Admin API - supports both GET and POST
        const method = field.apiMethod || 'GET';
        
        if (method === 'GET') {
          const queryParams: any = {
            ...field.apiParams,
          };

          // Add search filter if searchTerm exists
          if (searchTerm) {
            queryParams.filter = {
              [`${searchField}_has`]: searchTerm,
            };
          }

          // Use type assertion to bypass TypeScript union type issues
          const getMethod = client.GET as any;
          response = await getMethod(field.apiEndpoint, {
            params: {
              query: queryParams,
            },
          });
        } else {
          const bodyParams = {
            ...field.apiParams,
          };

          if (searchTerm) {
            bodyParams.filter = {
              [searchField]: searchTerm,
              [`${searchField}_like`]: searchTerm,
            };
          }

          // Use type assertion to bypass TypeScript union type issues
          const postMethod = client.POST as any;
          response = await postMethod(field.apiEndpoint, {
            body: bodyParams,
          });
        }
      } else {
        // App API - always uses POST with search structure
        const filters: API.SearchFilter[] = [...(field.searchFilters || [])];

        // Add search filter if searchTerm exists
        if (searchTerm) {
          filters.push({
            field: searchField,
            op: 'contains',
            value: searchTerm,
          });
        }

        const searchRequest: API.SearchRequest = {
          filters,
          page: 1,
          pageSize: 100, // Default page size
          ...(field.apiParams || {}),
        };

        // Use type assertion to bypass TypeScript union type issues
        const postMethod = client.POST as any;
        response = await postMethod(field.apiEndpoint, {
          body: searchRequest,
        });
      }

      if (response.error) {
        console.error('Error fetching options:', response.error);
        return;
      }

      // Handle different response structures
      let items: any[] = [];
      if (Array.isArray(response.data)) {
        items = response.data;
      } else if (response.data?.items && Array.isArray(response.data.items)) {
        items = response.data.items;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        items = response.data.data;
      }

      const formattedOptions = items.map((item: any) => ({
        label: item[labelField] || item.name || item.label || String(item[valueField]),
        value: item[valueField] || item.id || item.value,
      }));

      setOptions(formattedOptions);
    } catch (error) {
      console.error('Error fetching API options:', error);
    } finally {
      setLoading(false);
    }
  }, [
    field.apiEndpoint, 
    field.apiMethod, 
    field.apiParams, 
    field.labelField, 
    field.valueField, 
    field.searchField,
    field.searchFilters,
    field.isAdminApi,
    field.type
  ]);

  // Combined effect: initial load and search when debounced value changes
  useEffect(() => {
    fetchOptions(debouncedSearchValue);
  }, [debouncedSearchValue, fetchOptions]);

  const handleSearch = useCallback((value: string) => {
    setSearchValue(value);
  }, []);

  return { options: mergedOptions, loading, handleSearch };
};

const ModelField = ({
  field,
  control,
  setValue,
  errors
} : ModelFieldProps) => {
  const { RangePicker } = DatePicker
  const { id: itemId } = useParams()
  const { options: apiOptions, loading: apiLoading, handleSearch } = useApiOptions(field);

  return (
    <>
      
      {!field?.hideInForm && field?.type == 'text' && (
        <Controller
          name={field?.name}
          control={control}
          render={({ field: controllerField, fieldState }) => (
            <AntdForm.Item
              label={field?.label}
              validateStatus={fieldState.invalid ? 'error' : undefined}
              help={fieldState.error?.message}
            >
              <Input
                {...controllerField}
                {...field?.option}
                disabled={field?.readonly || (!!itemId && field?.editable === false)}
                placeholder={field?.placeholder}
              />
            </AntdForm.Item>
          )}
        />
      )}
      {!field?.hideInForm && field?.type == 'number' && (
        <Controller
          name={field?.name}
          control={control}
          render={({ field: controllerField, fieldState }) => (
            <AntdForm.Item
              label={field?.label}
              validateStatus={fieldState.invalid ? 'error' : undefined}
              help={fieldState.error?.message}
            >
              <InputNumber
                {...controllerField}
                {...field?.option}
                disabled={field?.readonly || (!!itemId && field?.editable === false)}
                placeholder={field?.placeholder}
                style={{ width: '100%' }}
              />
            </AntdForm.Item>
          )}
        />
      )}
      {!field?.hideInForm && field?.type == 'password' && (
        <FormItem control={control} name={field?.name} label={field?.label}>
          <Input.Password {...field?.option} disabled={field?.readonly || (!!itemId && field?.editable === false)}/>
        </FormItem>
      )}
      {!field?.hideInForm && field?.type == 'date' && (
        <FormItem 
          control={control} 
          name={field?.name} 
          label={field?.label}
          overrideFieldOnChange={(value) => {
            // Convert dayjs object to Date object for validation
            return value && typeof value.toDate === 'function' ? value.toDate() : value;
          }}
        >
          <DatePicker  {...field?.option} disabled={field?.readonly || (!!itemId && field?.editable === false)}/>
        </FormItem>
      )}
      {!field?.hideInForm && field?.type == 'dateRange' && (
        <FormItem 
          control={control} 
          name={field?.name} 
          label={field?.label}
          overrideFieldOnChange={(value) => {
            try {
              
              // Handle RangePicker value conversion
              if (value === null || value === undefined) {
                return undefined;
              }
              
              if (Array.isArray(value)) {
                
                // Handle partial selection (when user has selected start date but not end date yet)
                if (value.length === 2 && value.some(date => date === null || date === undefined)) {
                  return undefined;
                }
                
                if (value.length === 2) {
                  const dates = value.map((date, index) => {
                    
                    if (!date) {
                      return null;
                    }
                    
                    try {
                      // Check if it's a dayjs object with toDate method
                      if (date && typeof date.toDate === 'function') {
                        const converted = date.toDate();
                        return converted;
                      }
                      
                      // Check if it's already a Date object
                      if (date instanceof Date) {
                        return date;
                      }
                      
                      // Try to parse as a regular date
                      const fallback = new Date(date);
                      if (!isNaN(fallback.getTime())) {
                        return fallback;
                      } else {
                        return null;
                      }
                    } catch (error) {
                      console.error(`DateRange overrideFieldOnChange - error processing date ${index}:`, error);
                      return null;
                    }
                  });
                  
                  // Only return dates if both are valid
                  if (dates[0] && dates[1]) {
                    return dates;
                  } else {
                    return undefined;
                  }
                }
              }
              
              return undefined;
              
            } catch (error) {
              console.error('DateRange overrideFieldOnChange - caught error:', error);
              return undefined;
            }
          }}
        >
          {(() => {
            // Create a safe wrapper around RangePicker to prevent internal errors
            const SafeRangePicker = ({ value, ...props }: any) => {
              
              // Ensure RangePicker only gets valid values
              let safeValue = null;
              
              if (Array.isArray(value) && value.length === 2) {
                // Check if both values are valid dayjs objects
                const areValidDayjs = value.every(v => v && typeof v.isValid === 'function' && v.isValid());
                if (areValidDayjs) {
                  safeValue = value;
                } else {
                  // Check if they are Date objects that we can convert to dayjs
                  const areValidDates = value.every(v => v instanceof Date && !isNaN(v.getTime()));
                  if (areValidDates) {
                    // Convert Date objects back to dayjs for display
                    safeValue = value.map((date: Date) => dayjs(date));
                  } else {
                    console.log('SafeRangePicker - invalid value type, using null');
                  }
                }
              }
              
              return (
                <RangePicker
                  {...props}
                  value={safeValue}
                />
              );
            };
            
            return (
              <SafeRangePicker
                disabled={field?.readonly || (!!itemId && field?.editable === false)}
                {...field?.option}
              />
            );
          })()}
        </FormItem>
      )}
      {!field?.hideInForm && field?.type == 'select' && (
        <Controller
          name={field?.name}
          control={control}
          render={({ field: controllerField, fieldState }) => (
            <AntdForm.Item
              label={field?.label}
              validateStatus={fieldState.invalid ? 'error' : undefined}
              help={fieldState.error?.message}
            >
              <Select
                {...controllerField}
                showSearch
                allowClear
                loading={apiLoading}
                placeholder={field?.placeholder}
                optionFilterProp="children"
                // Use custom search for API endpoints, default filter for static data
                filterOption={field.apiEndpoint ? false : ((input, option) => (option?.label as string || '')?.toLowerCase().includes(input?.toLowerCase()))}
                onSearch={field.apiEndpoint ? handleSearch : undefined}
                // Use merged options for API endpoints, or static data for non-API fields
                options={field.apiEndpoint ? apiOptions : field?.data}
                disabled={field?.readonly || (!!itemId && field?.editable === false)}
                mode={field?.mode}
                notFoundContent={apiLoading ? 'Loading...' : 'No data'}
              />
            </AntdForm.Item>
          )}
        />
      )}
      {!field?.hideInForm && field?.type == 'checkbox' && (
        <FormItem name={field?.name} control={control} valuePropName='checked'>
          <Checkbox disabled={field?.readonly || (!!itemId && field?.editable === false)}>{field?.label}</Checkbox>
        </FormItem>
      )}
      {!field?.hideInForm && field?.type == 'textarea' && (
        <FormItem control={control} name={field?.name} label={field?.label}>
          <Input.TextArea  {...field?.option} disabled={field?.readonly || (!!itemId && field?.editable === false)}/>
        </FormItem>
      )}
      {!field?.hideInForm && field?.type == 'richtext' && (
        <>
          <label>{field?.label}</label>
          <Editor
            name={field?.name}
            setValue={setValue}
            errors={errors}
            defaultValue={field?.defaultValue}
            control={control}
          />
        </>
      )}
      {!field?.hideInForm && field?.type == 'json' && (
        <>
          <label>{field?.label}</label>
          <JSONField
            name={field?.name}
            setValue={setValue}
            errors={errors}
            defaultValue={field?.defaultValue}
            control={control}
          />
        </>
      )}
      {!field?.hideInForm && field?.type == 'arrayGroup' && (
        <ArrayGroup
          field={field}
          control={control}
          setValue={setValue}
          errors={errors}
        />
      )}
    </>
  )
}

export default ModelField